import { useEffect, useMemo, useRef, useState } from "react";
import { getInlineLoader } from "../Helpers";
import Footer from "../ones/Footer";
import { FaPlane, FaPrint, FaTruck } from "react-icons/fa6";
import { MdOutlineWarehouse } from "react-icons/md";
import { IoShieldCheckmarkOutline } from "react-icons/io5";

import banner4 from "../assets/banner4.jpg";
import banner5 from "../assets/banner5.jpg";
import banner6 from "../assets/banner6.jpg";
import banner7 from "../assets/banner7.jpg";
import banner8 from "../assets/banner8.jpg";
import printing from "../assets/print.jpg";
import supplies from "../assets/office.jpg";
import office from "../assets/computer.jpg";
import computer from "../assets/supplies.jpg";
import bg from "../assets/bg.jpg";
import tv from "../assets/tv.jpg";
import ac from "../assets/ac.jpg";
import cctv from "../assets/cctv.jpg";
import money from "../assets/money.jpg";
import furniture from "../assets/furniture.jpg";

/* ------------------------------------------------------------------ */
/* Scroll-reveal utility                                              */
/* ------------------------------------------------------------------ */
function useReveal(options = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px", ...options }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return [ref, visible];
}

function Reveal({ as: Tag = "div", children, delay = 0, className = "" }) {
  const [ref, visible] = useReveal();
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? "is-visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                               */
/* ------------------------------------------------------------------ */
export default function View() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(true);
  }, []);

  const logisticsServices = useMemo(
    () => [
      {
        title: "Air & Ocean Cargo",
        text: "A robust maritime and aviation network linking the world’s oceans and skies.",
        image: banner4,
        icon: <FaPlane />,
      },
      {
        title: "Customs Clearance",
        text: "Complete regulatory compliance and processing support for smooth imports and exports.",
        image: banner5,
        icon: <IoShieldCheckmarkOutline />,
      },
      {
        title: "Cross-Border Trucking",
        text: "Safe, reliable, and secure regional road freight tailored to route realities.",
        image: banner6,
        icon: <FaTruck />,
      },
      {
        title: "Train Cargo",
        text: "Efficient railway movement for bulk, heavy, and transit cargo across the region.",
        image: banner7,
        icon: <FaTruck />,
      },
      {
        title: "Project Cargo",
        text: "Specialized logistics architecture for machinery, events, and complex projects.",
        image: bg,
        icon: <MdOutlineWarehouse />,
      },
      {
        title: "Cold Chain Logistics",
        text: "Temperature-controlled supply chains optimized for perishable or sensitive goods.",
        image: banner8,
        icon: <MdOutlineWarehouse />,
      },
      {
        title: "Warehousing & Cargo Handling",
        text: "Secure storage, vehicle handling, and port-related service support.",
        image: banner4,
        icon: <MdOutlineWarehouse />,
      },
      {
        title: "School & Staff Bus Services",
        text: "Safe, premium transit options for elite institutions and corporate teams.",
        image: banner5,
        icon: <FaTruck />,
      },
    ],
    []
  );

  const supplyServices = useMemo(
    () => [
      {
        title: "Large Format Printing",
        text: "Canvas prints, banners, billboards, decor layouts, blueprints, and POS displays.",
        image: printing,
        icon: <FaPrint />,
      },
      {
        title: "Stationery & Office Supplies",
        text: "Bulk office essentials for corporate teams, schools, and public institutions.",
        image: supplies,
        icon: <FaPrint />,
      },
      {
        title: "Office Machines & Accessories",
        text: "Printers, copiers, thermal rolls, ink, cartridges, and support accessories.",
        image: office,
        icon: <FaPrint />,
      },
      {
        title: "Computer & IT Supplies",
        text: "Computers, desktops, consumer electronics, and audiovisual equipment.",
        image: computer,
        icon: <FaPrint />,
      },
      {
        title: "Air Conditioning Systems",
        text: "Supply and professional installation of high-quality AC units for corporate and residential spaces.",
        image: ac,
        icon: <MdOutlineWarehouse />,
      },
      {
        title: "Security Systems Configuration",
        text: "Sales and expert configuration of advanced security systems, access controls, and surveillance.",
        image: cctv,
        icon: <IoShieldCheckmarkOutline />,
      },
      {
        title: "Displays, Projectors & Cameras",
        text: "Premium flat screens, high-definition projectors, and professional cameras for all visual needs.",
        image: tv,
        icon: <FaPrint />,
      },
      {
        title: "Money Counting Machines",
        text: "Accurate, fast, and reliable currency counters and authenticators for seamless financial management.",
        image: money,
        icon: <IoShieldCheckmarkOutline />,
      },
      {
        title: "Office Furniture",
        text: "Ergonomic and modern office furniture designed to create productive, comfortable workspaces.",
        image: furniture,
        icon: <MdOutlineWarehouse />,
      },
    ],
    []
  );

  if (!ready) {
    return <div className="container mSupportLoading">{getInlineLoader()}</div>;
  }

  const renderServiceGrid = (services) => (
    <div className="corp-services-grid">
      {services.map((service, index) => (
        <Reveal key={service.title} delay={index * 60} as="article" className="corp-service-item h-100">
          <figure className="corp-service-media">
            <img src={service.image} alt={service.title} loading="lazy" />
            <span className="corp-service-index">{String(index + 1).padStart(2, "0")}</span>
            <span className="corp-service-icon">{service.icon}</span>
          </figure>
          <div className="corp-service-content bg-light p-3">
            <h3 className="text-primary">{service.title}</h3>
            <p>{service.text}</p>
          </div>
        </Reveal>
      ))}
    </div>
  );

  return (
    <section className="corp-page">
      {/* ---------- MODERN HERO (Title Section Only) ---------- */}
      <header className="mod-hero">
        <style>{`
          .mod-hero {
            position: relative;
            min-height: 50vh;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            padding: 4rem 1.25rem 3rem;
            overflow: hidden;
            background-color: #0f172a; /* Deep Navy Slate */
            font-family: system-ui, -apple-system, sans-serif;
          }
          @media (min-width: 768px) {
            .mod-hero { padding: 6rem 1.5rem 4rem; }
          }
          .mod-hero-bg {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            z-index: 0;
          }
          .mod-hero-img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transform: scale(1.05);
            animation: slowZoom 20s ease-out forwards;
          }
          .mod-hero-overlay {
            position: absolute;
            inset: 0;
            background: linear-gradient(180deg, rgba(15, 23, 42, 0.7) 0%, #0f172a 100%);
          }
          .mod-hero-content {
            position: relative;
            z-index: 1;
            max-width: 800px;
          }
          .mod-kicker {
            font-size: 0.85rem;
            text-transform: uppercase;
            letter-spacing: 0.15em;
            color: #60a5fa;
            font-weight: 600;
            margin-bottom: 1rem;
            display: block;
          }
          .mod-hero-title {
            font-size: clamp(2.2rem, 6vw, 4rem);
            font-weight: 800;
            color: #ffffff;
            margin-bottom: 1.5rem;
            letter-spacing: -0.02em;
            line-height: 1.1;
          }
          .mod-hero-title em {
            color: #3b82f6; /* Bright Blue */
            font-style: normal;
          }
          .mod-hero-lead {
            font-size: clamp(1rem, 2.5vw, 1.25rem);
            color: #94a3b8;
            line-height: 1.6;
            font-weight: 400;
          }
          @keyframes slowZoom {
            from { transform: scale(1); }
            to { transform: scale(1.1); }
          }
        `}</style>

        <div className="mod-hero-bg">
          <img src={bg} alt="Our Services" className="mod-hero-img" />
          <div className="mod-hero-overlay" />
        </div>
        
        <div className="container mod-hero-content">
          <Reveal delay={80}>
            <h1 className="mod-hero-title">
              Our <em>Services</em>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mod-hero-lead">
              Global logistics & freight forwarding, alongside premium printing, stationery, and technology supplies.
            </p>
          </Reveal>
        </div>
      </header>

      {/* ---------- DIVISION A ---------- */}
      <section className="pb-5 pt-5">
        <div className="container">
          <Reveal>
            <div className="corp-division-head mb-5">
              <h1 className="font-bold">Logistics & Freight Forwarding</h1>
            </div>
          </Reveal>
          {renderServiceGrid(logisticsServices)}
        </div>
      </section>

      {/* ---------- DIVISION B ---------- */}
      <section className="corp-section corp-section-alt">
        <div className="container">
          <Reveal>
            <div className="corp-division-head mb-5">
              <h1 className="text-primary font-bold">Facility, Printing & Technology Solutions</h1>
            </div>
          </Reveal>
          {renderServiceGrid(supplyServices)}
        </div>
      </section>

      <Footer />
    </section>
  );
}